import { useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import toast from "react-hot-toast";
import useGet from "../read/useGet";
import usePut from "../put/usePut";

export const usePettyCashFundRequests = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  const initialFilters = {
    limit: 10,
    page_number: parseInt(searchParams.get("page_number")) || 0,
    search_query: searchParams.get("search_query") || "",
    approval_status: searchParams.get("approval_status") || "",
    from_date: searchParams.get("from_date") || "",
    to_date: searchParams.get("to_date") || "",
  };

  const [searchQuery, setSearchQuery] = useState(initialFilters.search_query);

  const {
    list: fundRequestList,
    paramsObject: filters,
    setGetListParams: setFilters,
  } = useGet({
    url: "petty-cash/fund_request_list",
    initialData: initialFilters,
  });

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const debouncedSearch = useRef(
    debounce((value) => {
      const updated = { ...filtersRef.current, search_query: value, page_number: 0 };
      setFilters(updated);
      setSearchParams(updated);
    }, 300)
  ).current;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleFilterChange = (field, value) => {
    const updated = { ...filters, [field]: value, page_number: 0 };
    setFilters(updated);
    setSearchParams(updated);
  };

  const handlePageClick = (val) => {
    const updated = { ...filters, page_number: val.selected };
    setFilters(updated);
    setSearchParams(updated);
  };

  const { handleAuth: createRequestMutation } = usePut({
    url: "petty-cash/create_fund_request",
    refreshUrl: ["petty-cash/fund_request_list"],
    onSuccess: () => {
      toast.success("Fund request submitted successfully");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to submit fund request");
    },
  });
  const { mutate: createRequest, isLoading: isCreating } = createRequestMutation;

  const { handleAuth: updateRequestMutation } = usePut({
    url: "petty-cash/update_fund_request",
    refreshUrl: ["petty-cash/fund_request_list"],
    onSuccess: () => {
      toast.success("Fund request updated successfully");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to update fund request");
    },
  });
  const { mutate: updateRequest, isLoading: isUpdating } = updateRequestMutation;

  const { handleAuth: deleteRequestMutation } = usePut({
    url: "petty-cash/delete_fund_request",
    refreshUrl: ["petty-cash/fund_request_list"],
    onSuccess: () => {
      toast.success("Fund request deleted successfully");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to delete fund request");
    },
  });
  const { mutate: deleteRequest, isLoading: isDeleting } = deleteRequestMutation;

  const { handleAuth: approveRequestMutation } = usePut({
    url: "petty-cash/approve_fund_request",
    refreshUrl: [
      "petty-cash/fund_request_list",
      "petty-cash/statistics",
      "petty-cash/transaction_listing",
    ],
    onSuccess: () => {
      toast.success("Fund request status updated successfully");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to update fund request status");
    },
  });
  const { mutate: approveRequest, isLoading: isApproving } = approveRequestMutation;

  const { handleAuth: markAsPaidMutation } = usePut({
    url: "petty-cash/mark_fund_request_as_paid",
    refreshUrl: [
      "petty-cash/fund_request_list",
      "petty-cash/statistics",
      "petty-cash/transaction_listing",
    ],
    onSuccess: () => {
      toast.success("Fund request marked as paid successfully");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to mark fund request as paid");
    },
  });
  const { mutate: markAsPaid, isLoading: isMarkingAsPaid } = markAsPaidMutation;

  const {
    list: singleRequest,
    setGetListParams: setViewRequestParams,
  } = useGet({
    url: "petty-cash/view_fund_request",
    initialData: { request_id: selectedRequestId },
    enabled: !!selectedRequestId,
  });

  const handleViewDetails = (requestId) => {
    setSelectedRequestId(requestId);
    setViewRequestParams({ request_id: requestId });
  };

  return {
    fundRequestList,
    filters,
    searchQuery,
    selectedRequestId,
    setSelectedRequestId,
    singleRequest,
    isCreating,
    isUpdating,
    isDeleting,
    isApproving,
    markAsPaid,
    isMarkingAsPaid,
    handleSearchChange,
    handleFilterChange,
    handlePageClick,
    handleViewDetails,
    createRequest,
    updateRequest,
    deleteRequest,
    approveRequest,
  };
};
